import {
    GetFundingRequirementsCreditFacilityResponse,
    GetFundingRequirementsFlexi24Response,
    GetFundingRequirementsIwocapayResponse,
    GetFundingRequirementsMerchantCashAdvanceResponse
} from '@iwoca/lapi-client/edge';
import _ from 'lodash';

export type TDeserialisedFundingRequirements = {
    type:
        | 'DEBIT_CARD'
        | 'BANK_DETAILS'
        | 'PENNY_TRANSFER'
        | 'IDENTITY_QUESTION'
        | 'ONFIDO_VERIFICATION'
        | 'ONFIDO_VIDEO_VERIFICATION'
        | 'IDVERSE_VERIFICATION'
        | 'MAJOR_SHAREHOLDER_CHECK'
        | 'BIP'
        | 'APPLICANT_CALL'
        | 'COMMISSION_CONSENT'
        | 'UPLOAD_GEWERBESCHEIN'
        | 'UPLOAD_BANK_EVIDENCE';
    status: 'OPTIONAL' | 'REQUIRED' | 'COMPLETED' | 'ERROR' | 'PENDING';
    errors?: { code: string; detail: string }[];
}[];

export type TFundingRequirements =
    | GetFundingRequirementsCreditFacilityResponse
    | GetFundingRequirementsFlexi24Response
    | GetFundingRequirementsIwocapayResponse
    | GetFundingRequirementsMerchantCashAdvanceResponse;

export type TFundingRequirement =
    | GetFundingRequirementsCreditFacilityResponse['data'][0]
    | GetFundingRequirementsFlexi24Response['data'][0]
    | GetFundingRequirementsIwocapayResponse['data'][0]
    | GetFundingRequirementsMerchantCashAdvanceResponse['data'][0];

export type TFundingRequirementErrors =
    TDeserialisedFundingRequirements[number]['errors'];

export type TFundingRequirementStatus =
    TDeserialisedFundingRequirements[number]['status'];

type TFundingRequirementType = TDeserialisedFundingRequirements[number]['type'];
function getActionFromFundingRequirement(
    funding_requirement: TFundingRequirement
): TFundingRequirementType {
    switch (funding_requirement.recommended_resolution_method) {
        case 'add_default_payment_method':
            return 'DEBIT_CARD';
        case 'link_bank_account':
        case 'check_linked_direct_debit':
        case 'check_funding_bank_account_is_valid':
            return 'BANK_DETAILS';
        case 'complete_penny_transfer':
        case 'complete_penny_transfer_with_signature':
            return 'PENNY_TRANSFER';
        case 'onfido_check':
            return 'ONFIDO_VERIFICATION';
        case 'onfido_video_check':
            return 'ONFIDO_VIDEO_VERIFICATION';
        case 'idverse_check':
            return 'IDVERSE_VERIFICATION';
        case 'major_shareholders_check':
            return 'MAJOR_SHAREHOLDER_CHECK';
        case 'check_customer_does_not_exceed_bip_cap':
            return 'BIP';
        case 'check_commission_consent_checkbox':
            return 'COMMISSION_CONSENT';
        case 'gewerbeschein_hochladen':
            return 'UPLOAD_GEWERBESCHEIN';
        case 'upload_bank_statement':
        case 'link_bank_account_via_open_banking':
            return 'UPLOAD_BANK_EVIDENCE';
        default:
            return 'APPLICANT_CALL';
    }
}

function showUniqueFundingRequirementsWithCorrectStatus(
    fundingRequirements: TDeserialisedFundingRequirements
): TDeserialisedFundingRequirements {
    return fundingRequirements.reduce((requirements: any, requirement: any) => {
        const existingDuplicateRequirement = _.findIndex(requirements, {
            type: requirement.type
        });
        if (existingDuplicateRequirement === -1) {
            return [...requirements, requirement];
        }

        const STATUS_PRIORITY_ORDER = {
            REQUIRED: 1,
            ERROR: 2,
            PENDING: 3,
            COMPLETED: 4
        };

        if (
            STATUS_PRIORITY_ORDER[
                requirements[existingDuplicateRequirement]
                    .status as keyof typeof STATUS_PRIORITY_ORDER
            ] >
            STATUS_PRIORITY_ORDER[
                requirement.status as keyof typeof STATUS_PRIORITY_ORDER
            ]
        ) {
            requirements[existingDuplicateRequirement] = requirement;
        }

        return requirements;
    }, []);
}

const completeApplicationStatus = {
    optional: 'OPTIONAL',
    required: 'REQUIRED',
    completed: 'COMPLETED',
    error: 'ERROR',
    pending: 'PENDING'
};

const isUploadBankEvidenceRequirement = (
    requirement: GetFundingRequirementsCreditFacilityResponse['data'][0]
) =>
    requirement.recommended_resolution_method === 'upload_bank_statement' ||
    requirement.recommended_resolution_method ===
        'link_bank_account_via_open_banking';

const getCompleteApplicationStatus = (
    requirement: GetFundingRequirementsCreditFacilityResponse['data'][0],
    { isInUploadBankEvidenceTest }: { isInUploadBankEvidenceTest: boolean }
) => {
    if (requirement.status === 'required' && requirement.is_optional === true) {
        return completeApplicationStatus.optional;
    }

    // For users in the upload bank evidence test, treat manual_review as COMPLETED
    // for the bank evidence requirement (upload_bank_statement / link_bank_account_via_open_banking).
    if (
        isInUploadBankEvidenceTest &&
        (requirement.status as string) === 'manual_review' &&
        isUploadBankEvidenceRequirement(requirement)
    ) {
        return completeApplicationStatus.completed;
    }

    return completeApplicationStatus[requirement.status];
};

const getErrorsFromFundingRequirement = (requirement: TFundingRequirement) => {
    if (
        requirement.recommended_resolution_method !==
            'check_funding_bank_account_is_valid' ||
        requirement.status === 'completed'
    ) {
        return [];
    }
    const resolutionMethods = requirement.resolution_methods
        ? requirement.resolution_methods
        : [];

    const resolutionMethodErrors = resolutionMethods.flatMap((resMethod) => {
        return resMethod.errors || [];
    });
    return resolutionMethodErrors;
};

export const deserialiseFundingRequirements = (
    funding_requirements: TFundingRequirements['data'],
    {
        isInUploadBankEvidenceTest = false
    }: { isInUploadBankEvidenceTest?: boolean } = {}
): TDeserialisedFundingRequirements => {
    const deserialisedFundingRequirements = funding_requirements
        .filter(
            (requirement) =>
                requirement.category !== 'contractual' ||
                (requirement.category === 'contractual' &&
                    [
                        'complete_repayment_pledge',
                        'check_commission_consent_checkbox'
                    ].includes(requirement.recommended_resolution_method))
        )
        .filter((req) => !req.is_optional)
        .map((funding_requirement) => ({
            type: getActionFromFundingRequirement(
                funding_requirement
            ) as TFundingRequirementType,
            status: getCompleteApplicationStatus(funding_requirement, {
                isInUploadBankEvidenceTest
            }) as TFundingRequirementStatus,
            errors: getErrorsFromFundingRequirement(
                funding_requirement
            ) as TFundingRequirementErrors
        }));

    const uniqueFundingRequirements =
        showUniqueFundingRequirementsWithCorrectStatus(
            deserialisedFundingRequirements
        );

    return uniqueFundingRequirements;
};
